import { useQuery } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { adminService } from '@/lib/api/services/admin.service';
import { Loader2, MessageCircle, AlertTriangle, X, Info, Eye, ChevronLeft, ChevronRight, ChevronDown, Search, Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';

interface ConversationAnalysis {
  overall_sentiment?: 'positive' | 'neutral' | 'negative' | null;
  sentiment_score?: number | null;
  average_sentiment?: number | null;
  reply_rate_24h?: number | null;
  average_response_time_hours?: number | null;
  total_exchanges?: number | null;
  is_flagged?: boolean;
  flag_reason?: string | null;
  ai_tags?: string[];
}

interface ConversationParticipant {
  id: string;
  conversation_id: string;
  user_id: string;
  role: 'genius' | 'client';
  display_name?: string | null;
  profile_picture_url?: string | null;
  profile_id?: string | null;
}

interface Conversation {
  id: string;
  created_at: string;
  last_message_at?: string | null;
  conversation_participants?: ConversationParticipant[];
  conversation_analysis?: ConversationAnalysis | ConversationAnalysis[] | null;
}

interface ConversationFilters {
  startDate?: string;
  endDate?: string;
  flagged?: boolean;
  role?: 'genius' | 'client';
  sentiment?: 'positive' | 'neutral' | 'negative';
  engagement?: 'low' | 'medium' | 'high';
  tags?: string[];
}

interface AdminChatViewerProps {
  initialFilters?: ConversationFilters;
  onConversationSelect?: (conversationId: string) => void;
}

export function AdminChatViewer({ initialFilters, onConversationSelect }: AdminChatViewerProps) {
  const [page, setPage] = useState(1);
  const limit = 20;

  // Helper function to get default last 30 days dates
  const getDefaultDates = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatDate(thirtyDaysAgo),
      endDate: formatDate(today)
    };
  };

  // Compute initial values synchronously to avoid race conditions with useEffect
  const defaults = getDefaultDates();
  const initStartDate = initialFilters?.startDate || defaults.startDate;
  const initEndDate = initialFilters?.endDate || defaults.endDate;

  // UI state
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [tagsPopoverOpen, setTagsPopoverOpen] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');

  // Temporary filter states (before applying)
  const [tempStartDate, setTempStartDate] = useState<string>(initStartDate);
  const [tempEndDate, setTempEndDate] = useState<string>(initEndDate);
  const [tempFlaggedFilter, setTempFlaggedFilter] = useState<boolean | undefined>(initialFilters?.flagged);
  const [tempRoleFilter, setTempRoleFilter] = useState<'genius' | 'client' | undefined>(initialFilters?.role);
  const [tempSentimentFilter, setTempSentimentFilter] = useState<'positive' | 'neutral' | 'negative' | 'all' | undefined>(initialFilters?.sentiment || undefined);
  const [tempEngagementFilter, setTempEngagementFilter] = useState<'low' | 'medium' | 'high' | 'all' | undefined>(initialFilters?.engagement || undefined);
  const [tempSelectedTags, setTempSelectedTags] = useState<string[]>(initialFilters?.tags || []);

  // Applied filter states (used for queries)
  const [startDate, setStartDate] = useState<string>(initStartDate);
  const [endDate, setEndDate] = useState<string>(initEndDate);
  const [flaggedFilter, setFlaggedFilter] = useState<boolean | undefined>(initialFilters?.flagged);
  const [roleFilter, setRoleFilter] = useState<'genius' | 'client' | undefined>(initialFilters?.role);
  const [sentimentFilter, setSentimentFilter] = useState<'positive' | 'neutral' | 'negative' | undefined>(initialFilters?.sentiment);
  const [engagementFilter, setEngagementFilter] = useState<'low' | 'medium' | 'high' | undefined>(initialFilters?.engagement);
  const [selectedTags, setSelectedTags] = useState<string[]>(initialFilters?.tags || []);

  const { data: availableTags = [] } = useQuery({
    queryKey: ['admin', 'conversation-tags'],
    queryFn: () => adminService.getConversationTags(),
  });

  const { data: conversations = [], isLoading: loadingConversations } = useQuery({
    queryKey: ['admin', 'conversations', startDate, endDate, flaggedFilter, roleFilter, selectedTags, sentimentFilter, engagementFilter, page],
    queryFn: () =>
      adminService.getAllConversations({
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        flagged: flaggedFilter,
        role: roleFilter,
        tags: selectedTags.length > 0 ? selectedTags : undefined,
        sentiment: sentimentFilter,
        engagement: engagementFilter,
        limit,
        offset: (page - 1) * limit,
      }),
  });

  // Helper to normalize conversation_analysis (can be object or array)
  const getAnalysis = (analysis: ConversationAnalysis | ConversationAnalysis[] | null | undefined): ConversationAnalysis | null => {
    if (!analysis) return null;
    if (Array.isArray(analysis)) return analysis[0] || null;
    return analysis;
  };

  // All filtering is now done server-side, so we can use conversations directly
  const filteredConversations = conversations;

  const handleApplyFilters = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setFlaggedFilter(tempFlaggedFilter);
    setRoleFilter(tempRoleFilter);
    setSentimentFilter(tempSentimentFilter === 'all' ? undefined : tempSentimentFilter);
    setEngagementFilter(tempEngagementFilter === 'all' ? undefined : tempEngagementFilter);
    setSelectedTags(tempSelectedTags);
    setPage(1);
    setFiltersExpanded(false);
  };

  // Filtered tags for search
  const filteredTags = useMemo(() => {
    if (!tagSearchQuery) return availableTags;
    const query = tagSearchQuery.toLowerCase();
    return availableTags.filter(tag => tag.toLowerCase().includes(query));
  }, [availableTags, tagSearchQuery]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (startDate || endDate) count++;
    if (sentimentFilter) count++;
    if (engagementFilter) count++;
    if (flaggedFilter !== undefined) count++;
    if (roleFilter) count++;
    if (selectedTags.length > 0) count += selectedTags.length;
    return count;
  }, [startDate, endDate, sentimentFilter, engagementFilter, flaggedFilter, roleFilter, selectedTags]);

  const handleClearFilters = () => {
    setTempStartDate('');
    setTempEndDate('');
    setTempFlaggedFilter(undefined);
    setTempRoleFilter(undefined);
    setTempSentimentFilter(undefined);
    setTempEngagementFilter(undefined);
    setTempSelectedTags([]);
    
    setStartDate('');
    setEndDate('');
    setFlaggedFilter(undefined);
    setRoleFilter(undefined);
    setSentimentFilter(undefined);
    setEngagementFilter(undefined);
    setSelectedTags([]);
    setPage(1);
  };

  const handleTagToggle = (tag: string) => {
    setTempSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const getSentimentColor = (sentiment: string | null | undefined) => {
    if (sentiment === 'positive') return 'bg-emerald-100 text-emerald-700';
    if (sentiment === 'negative') return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-slate-700';
  };

  const hasActiveFilters = startDate || endDate || flaggedFilter !== undefined || roleFilter || selectedTags.length > 0 || sentimentFilter || engagementFilter;
  const hasTempFilters = tempStartDate || tempEndDate || tempFlaggedFilter !== undefined || tempRoleFilter || tempSelectedTags.length > 0 || (tempSentimentFilter && tempSentimentFilter !== 'all') || (tempEngagementFilter && tempEngagementFilter !== 'all');

  // Active filter chips
  const activeFilterChips = useMemo(() => {
    const chips: { label: string; onRemove: () => void }[] = [];
    if (startDate || endDate) {
      const dateLabel = startDate && endDate 
        ? `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`
        : startDate 
        ? `From ${new Date(startDate).toLocaleDateString()}`
        : `Until ${new Date(endDate).toLocaleDateString()}`;
      chips.push({
        label: dateLabel,
        onRemove: () => {
          setTempStartDate('');
          setTempEndDate('');
          setStartDate('');
          setEndDate('');
        },
      });
    }
    if (sentimentFilter) {
      chips.push({
        label: `Sentiment: ${sentimentFilter}`,
        onRemove: () => {
          setTempSentimentFilter(undefined);
          setSentimentFilter(undefined);
        },
      });
    }
    if (engagementFilter) {
      chips.push({
        label: `Engagement: ${engagementFilter}`,
        onRemove: () => {
          setTempEngagementFilter(undefined);
          setEngagementFilter(undefined);
        },
      });
    }
    if (flaggedFilter !== undefined) {
      chips.push({
        label: flaggedFilter ? 'Flagged' : 'Not Flagged',
        onRemove: () => {
          setTempFlaggedFilter(undefined);
          setFlaggedFilter(undefined);
        },
      });
    }
    if (roleFilter) {
      chips.push({
        label: `Role: ${roleFilter === 'genius' ? 'Freelancer' : 'Client'}`,
        onRemove: () => {
          setTempRoleFilter(undefined);
          setRoleFilter(undefined);
        },
      });
    }
    selectedTags.forEach(tag => {
      chips.push({
        label: `Tag: ${tag}`,
        onRemove: () => {
          setTempSelectedTags(prev => prev.filter(t => t !== tag));
          setSelectedTags(prev => prev.filter(t => t !== tag));
        },
      });
    });
    return chips;
  }, [startDate, endDate, sentimentFilter, engagementFilter, flaggedFilter, roleFilter, selectedTags]);

  const handleConversationClick = (conversationId: string) => {
    if (onConversationSelect) {
      onConversationSelect(conversationId);
    }
  };

  const handleParticipantClick = (e: React.MouseEvent, participant: ConversationParticipant) => {
    e.stopPropagation();
    if (participant.profile_id) {
      const url = participant.role === 'genius' 
        ? `/genius/${participant.profile_id}`
        : `/client/${participant.profile_id}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto">
        {/* Collapsible Filters */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-4">
          <Collapsible open={filtersExpanded} onOpenChange={setFiltersExpanded}>
            {/* Filter Header - Always Visible */}
            <div className="px-4 py-3">
              <div className="flex items-center justify-between">
                <CollapsibleTrigger asChild>
                  <button className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900">
                    <Filter className="w-4 h-4" />
                    <span>Filters</span>
                    {activeFilterCount > 0 && (
                      <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-[#4669ed] rounded-full">
                        {activeFilterCount}
                      </span>
                    )}
                    <ChevronDown className={cn("w-4 h-4 transition-transform", filtersExpanded && "rotate-180")} />
                  </button>
                </CollapsibleTrigger>

                {/* Active filter chips inline when collapsed */}
                {!filtersExpanded && activeFilterChips.length > 0 && (
                  <div className="flex gap-2 flex-wrap items-center flex-1 ml-4 mr-4">
                    {activeFilterChips.slice(0, 4).map((chip, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-xs font-medium bg-[rgba(70,105,237,0.10)] text-[#4669ed]"
                      >
                        {chip.label}
                        <button onClick={chip.onRemove} className="hover:text-[#3654c9]">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    {activeFilterChips.length > 4 && (
                      <span className="text-xs text-slate-500">+{activeFilterChips.length - 4} more</span>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  {hasActiveFilters && (
                    <Button
                      onClick={handleClearFilters}
                      variant="ghost"
                      size="sm"
                      className="text-xs h-7 px-2"
                    >
                      Clear
                    </Button>
                  )}
                  {!hasActiveFilters && (
                    <span className="text-xs text-slate-400">Last 30 days</span>
                  )}
                </div>
              </div>
            </div>

            {/* Expandable Filter Content */}
            <CollapsibleContent>
              <div className="px-4 pb-4 border-t border-slate-100 pt-4">
                {/* First Row: Date Range, Sentiment, Engagement */}
                <div className="grid grid-cols-4 gap-3 mb-3">
                  {/* Date Range */}
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500 mb-2 block">Date Range</Label>
                    <div className="flex items-center gap-2 bg-white rounded-[40px] border border-slate-200 px-4 h-10 shadow-sm hover:shadow-md transition-shadow w-full">
                      <input
                        type="date"
                        value={tempStartDate}
                        onChange={(e) => setTempStartDate(e.target.value)}
                        className="h-8 flex-1 text-sm bg-transparent border-none outline-none focus:ring-0 text-slate-700 cursor-pointer [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:w-4 [&::-webkit-calendar-picker-indicator]:h-4 [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-calendar-picker-indicator]:saturate-0 [&::-webkit-calendar-picker-indicator]:invert-[0.6]"
                      />
                      <span className="text-slate-400 text-sm flex-shrink-0">-</span>
                      <input
                        type="date"
                        value={tempEndDate}
                        onChange={(e) => setTempEndDate(e.target.value)}
                        min={tempStartDate}
                        className="h-8 flex-1 text-sm bg-transparent border-none outline-none focus:ring-0 text-slate-700 cursor-pointer [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:w-4 [&::-webkit-calendar-picker-indicator]:h-4 [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-calendar-picker-indicator]:saturate-0 [&::-webkit-calendar-picker-indicator]:invert-[0.6]"
                      />
                    </div>
                  </div>

                  {/* Sentiment */}
                  <div>
                    <Label className="text-xs text-slate-500 mb-2 block">Sentiment</Label>
                    <Select
                      value={tempSentimentFilter || 'all'}
                      onValueChange={(value) => {
                        setTempSentimentFilter(value === 'all' ? undefined : (value as 'positive' | 'neutral' | 'negative'));
                      }}
                    >
                      <SelectTrigger className="w-full rounded-[40px] bg-white border-slate-200 h-10 text-sm shadow-sm hover:shadow-md transition-shadow">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="positive">Positive</SelectItem>
                        <SelectItem value="neutral">Neutral</SelectItem>
                        <SelectItem value="negative">Negative</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Engagement */}
                  <div>
                    <Label className="text-xs text-slate-500 mb-2 block">Engagement</Label>
                    <Select
                      value={tempEngagementFilter || 'all'}
                      onValueChange={(value) => {
                        setTempEngagementFilter(value === 'all' ? undefined : (value as 'low' | 'medium' | 'high'));
                      }}
                    >
                      <SelectTrigger className="w-full rounded-[40px] bg-white border-slate-200 h-10 text-sm shadow-sm hover:shadow-md transition-shadow">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="low">Low (&lt;4 msgs)</SelectItem>
                        <SelectItem value="medium">Medium (4-10 msgs)</SelectItem>
                        <SelectItem value="high">High (10+ msgs)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Second Row: Flagged, Role, Tags */}
                <div className="grid grid-cols-3 gap-3">
                  {/* Tags - Searchable Dropdown */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Label className="text-xs text-slate-500">Tags</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-slate-400 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-xs">AI-generated based on conversation topics</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Popover open={tagsPopoverOpen} onOpenChange={setTagsPopoverOpen} modal={false}>
                      <PopoverTrigger asChild>
                        <button 
                          type="button"
                          className="w-full flex items-center justify-between gap-2 px-4 h-10 rounded-[40px] border border-slate-200 bg-white text-sm hover:bg-slate-50 shadow-sm hover:shadow-md transition-shadow"
                        >
                          <span className="text-slate-700 truncate">
                            {tempSelectedTags.length > 0 ? `${tempSelectedTags.length} tag${tempSelectedTags.length > 1 ? 's' : ''} selected` : 'Select tags...'}
                          </span>
                          <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent 
                        className="w-72 p-0 z-[9999] bg-white shadow-lg pointer-events-auto" 
                        align="start" 
                        onOpenAutoFocus={(e) => e.preventDefault()}
                        sideOffset={8}
                      >
                        <div className="p-3 space-y-2 border-b border-slate-100 flex-shrink-0">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-slate-900">Select Tags</span>
                            {tempSelectedTags.length > 0 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setTempSelectedTags([])}
                                className="h-auto p-1 text-xs hover:bg-slate-100"
                              >
                                Clear
                              </Button>
                            )}
                          </div>
                          <div className="relative">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400 pointer-events-none" />
                            <Input
                              placeholder="Search tags..."
                              value={tagSearchQuery}
                              onChange={(e) => setTagSearchQuery(e.target.value)}
                              className="pl-8 h-9 rounded-lg border-slate-200"
                            />
                          </div>
                        </div>
                        <div className="max-h-[200px] overflow-y-auto">
                          <div className="px-3 py-2 space-y-1">
                            {filteredTags.length === 0 ? (
                              <p className="text-sm text-slate-400 text-center py-4">No tags found</p>
                            ) : (
                              filteredTags.map((tag) => (
                                <div
                                  key={tag}
                                  className="flex items-center space-x-2 hover:bg-slate-50 p-2 rounded-md cursor-pointer transition-colors"
                                  onClick={() => handleTagToggle(tag)}
                                >
                                  <Checkbox
                                    checked={tempSelectedTags.includes(tag)}
                                    onCheckedChange={() => handleTagToggle(tag)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <label className="text-sm flex-1 cursor-pointer text-slate-700">
                                    {tag}
                                  </label>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                        {tempSelectedTags.length > 0 && (
                          <div className="border-t border-slate-100 p-3 flex-shrink-0">
                            <div className="flex flex-wrap gap-1">
                              {tempSelectedTags.slice(0, 3).map((tag) => (
                                <Badge key={tag} variant="secondary" className="text-xs bg-slate-100 text-slate-700 hover:bg-slate-200">
                                  {tag}
                                  <button
                                    type="button"
                                    onClick={() => handleTagToggle(tag)}
                                    className="ml-1 hover:text-destructive transition-colors"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </Badge>
                              ))}
                              {tempSelectedTags.length > 3 && (
                                <Badge variant="secondary" className="text-xs bg-slate-100 text-slate-700">
                                  +{tempSelectedTags.length - 3}
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                  {/* Flagged Status */}
                  <div>
                    <Label className="text-xs text-slate-500 mb-2 block">Flagged</Label>
                    <Select
                      value={tempFlaggedFilter === undefined ? 'all' : tempFlaggedFilter ? 'true' : 'false'}
                      onValueChange={(value) => {
                        setTempFlaggedFilter(value === 'all' ? undefined : value === 'true');
                      }}
                    >
                      <SelectTrigger className="w-full rounded-[40px] bg-white border-slate-200 h-10 text-sm shadow-sm hover:shadow-md transition-shadow">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="true">Flagged</SelectItem>
                        <SelectItem value="false">Not Flagged</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Role */}
                  <div>
                    <Label className="text-xs text-slate-500 mb-2 block">Role</Label>
                    <Select
                      value={tempRoleFilter || 'all'}
                      onValueChange={(value) => {
                        setTempRoleFilter(value === 'all' ? undefined : (value as 'genius' | 'client'));
                      }}
                    >
                      <SelectTrigger className="w-full rounded-[40px] bg-white border-slate-200 h-10 text-sm shadow-sm hover:shadow-md transition-shadow">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="genius">Freelancer</SelectItem>
                        <SelectItem value="client">Client</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Apply Button */}
                <div className="mt-4 flex justify-end">
                  <Button
                    onClick={handleApplyFilters}
                    disabled={!hasTempFilters}
                    className="rounded-[40px] bg-[#4669ed] hover:bg-[#3654c9] h-10 px-5 text-sm shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Apply Filters
                  </Button>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>

        {/* Table Section */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between px-4 py-4 border-b border-slate-200">
            <div>
              <h3 className="text-base font-semibold text-slate-900">All Conversations</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {filteredConversations.length > 0
                  ? `Showing ${filteredConversations.length} conversation${filteredConversations.length !== 1 ? 's' : ''}`
                  : 'No conversations found'}
              </p>
            </div>
          </div>
          <div>
            {loadingConversations ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : filteredConversations.length > 0 ? (
              <>
                {/* Column Headers */}
                <div className="bg-slate-50 border-b border-slate-200">
                  <div className="grid grid-cols-[2fr_0.8fr_0.8fr_0.8fr_0.7fr_1fr_0.6fr] px-4 py-3 gap-3">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Participants</div>
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Created</div>
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Msg</div>
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Sentiment</div>
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Eng.</div>
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Tags</div>
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider"></div>
                  </div>
                </div>

                {/* Table Rows */}
                <div className="divide-y divide-slate-100">
                  {filteredConversations.map((conv: Conversation) => {
                    const analysis = getAnalysis(conv.conversation_analysis);
                    const participants = conv.conversation_participants || [];
                    const geniusParticipant = participants.find((p: ConversationParticipant) => p.role === 'genius');
                    const clientParticipant = participants.find((p: ConversationParticipant) => p.role === 'client');
                    const totalExchanges = analysis?.total_exchanges || 0;

                    return (
                      <div
                        key={conv.id}
                        className="grid grid-cols-[2fr_0.8fr_0.8fr_0.8fr_0.7fr_1fr_0.6fr] px-4 py-3 gap-3 items-center hover:bg-slate-50/50 cursor-pointer transition-colors duration-150"
                      >

                        {/* Participants */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {geniusParticipant && (
                            <div
                              onClick={(e) => handleParticipantClick(e, geniusParticipant)}
                              className="flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-xs text-slate-700 cursor-pointer"
                            >
                              <Avatar className="w-4 h-4">
                                <AvatarImage src={geniusParticipant.profile_picture_url || undefined} />
                                <AvatarFallback className="bg-[#4669ed] text-white text-[7px]">
                                  {geniusParticipant.display_name?.charAt(0).toUpperCase() || 'G'}
                                </AvatarFallback>
                              </Avatar>
                              <span className="truncate max-w-[80px]">
                                {geniusParticipant.display_name || geniusParticipant.user_id.substring(0, 6)}
                              </span>
                            </div>
                          )}
                          {clientParticipant && (
                            <div
                              onClick={(e) => handleParticipantClick(e, clientParticipant)}
                              className="flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-xs text-slate-700 cursor-pointer"
                            >
                              <Avatar className="w-4 h-4">
                                <AvatarImage src={clientParticipant.profile_picture_url || undefined} />
                                <AvatarFallback className="bg-slate-500 text-white text-[7px]">
                                  {clientParticipant.display_name?.charAt(0).toUpperCase() || 'C'}
                                </AvatarFallback>
                              </Avatar>
                              <span className="truncate max-w-[80px]">
                                {clientParticipant.display_name || clientParticipant.user_id.substring(0, 6)}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Created Date */}
                        <div className="text-xs text-slate-600">
                          {new Date(conv.created_at).toLocaleDateString()}
                        </div>

                        {/* Last Message Date */}
                        <div className="text-xs text-slate-600">
                          {conv.last_message_at
                            ? new Date(conv.last_message_at).toLocaleDateString()
                            : '-'}
                        </div>

                        {/* Sentiment */}
                        <div>
                          {analysis?.overall_sentiment ? (
                            <span className={cn(
                              "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
                              getSentimentColor(analysis.overall_sentiment)
                            )}>
                              {analysis.overall_sentiment.charAt(0).toUpperCase() + analysis.overall_sentiment.slice(1)}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </div>

                        {/* Engagement */}
                        <div className="text-xs text-slate-600">
                          {analysis ? `${totalExchanges} msgs` : '-'}
                        </div>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-1">
                          {analysis?.ai_tags && analysis.ai_tags.length > 0 ? (
                            <>
                              {analysis.ai_tags.slice(0, 1).map((tag: string, idx: number) => (
                                <span key={idx} className="px-1.5 py-0.5 rounded bg-slate-100 text-xs text-slate-600 truncate max-w-[80px]">
                                  {tag}
                                </span>
                              ))}
                              {analysis.ai_tags.length > 1 && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-xs text-slate-600 cursor-help">
                                        +{analysis.ai_tags.length - 1}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                      <div className="flex flex-wrap gap-1">
                                        {analysis.ai_tags.slice(1).map((tag: string, idx: number) => (
                                          <span key={idx} className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-xs">
                                            {tag}
                                          </span>
                                        ))}
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                          {analysis?.is_flagged && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-100 text-xs text-red-700">
                              <AlertTriangle className="w-3 h-3" />
                            </span>
                          )}
                        </div>

                        {/* Actions */}
                        <div onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleConversationClick(conv.id)}
                            className="h-7 px-2 text-xs text-[#4669ed] hover:text-[#3654c9] hover:bg-[rgba(70,105,237,0.1)]"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                  <div className="text-xs text-slate-500">
                    Page {page}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="rounded-lg border-slate-200 h-7 text-xs"
                    >
                      <ChevronLeft className="w-3 h-3 mr-1" />
                      Prev
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={!conversations || conversations.length < limit}
                      className="rounded-lg border-slate-200 h-7 text-xs"
                    >
                      Next
                      <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <MessageCircle className="h-8 w-8 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No conversations found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
